import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import { register } from "../services/auth";

const RegisterPage: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [acceptPolicy, setAcceptPolicy] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [isSuccess, setIsSuccess] = useState(false);

    const passwordStrength = useMemo(() => {
        let score = 0;
        if (password.length >= 6) score += 1;
        if (/[A-Z]/.test(password)) score += 1;
        if (/[0-9]/.test(password)) score += 1;
        if (/[^A-Za-z0-9]/.test(password)) score += 1;
        return score;
    }, [password]);

    const strengthLabel = ["Weak password", "Okay", "Strong", "Excellent"][passwordStrength] || "Weak password";
    const strengthColors = ["text-red-300", "text-amber-300", "text-emerald-300", "text-emerald-200"];

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        if (!acceptPolicy) {
            setError("Please confirm that you agree to the Terms of Service and Privacy Policy.");
            return;
        }

        setIsLoading(true);

        try {
            await register(email.trim(), password);
            setIsSuccess(true);
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to create account. Please try again.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <AuthLayout
                title="Check your email"
                description="Use the link we sent to verify your account."
                ctaLabel="Log in"
                ctaHref="/login"
                ctaPrompt="Already verified?"
                accent="emerald"
            >
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-6 py-6 text-center text-emerald-200">
                    <p className="mb-4 text-lg font-semibold text-emerald-100">Only one step left!</p>
                    <p>
                        We've sent a verification link to <strong>{email}</strong>.
                        Please check your inbox (and spam folder) to complete your registration.
                    </p>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title="Create an account"
            description="Register to get API keys and manage your usage."
            ctaLabel="Log in"
            ctaHref="/login"
            ctaPrompt="Already have an account?"
            accent="sky"
            beforeCard={
                <div className="mb-6 rounded-xl border border-sky-400/40 bg-sky-400/15 px-4 py-3 text-center text-sm font-semibold text-sky-50 shadow shadow-sky-500/20">
                    Free credits on start
                </div>
            }
        >
            <div className="space-y-5">
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium text-slate-200">
                            Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            className="block w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/40"
                            placeholder="team@company.com"
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="password" className="text-sm font-medium text-slate-200">
                            Password
                        </label>
                        <input
                            id="password"
                            type="password"
                            required
                            autoComplete="new-password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            className="block w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/40"
                            placeholder="Minimum 6 characters"
                        />
                        {password.length > 0 && (
                            <p className={`text-xs ${strengthColors[passwordStrength - 1] || "text-red-300"}`}>
                                {strengthLabel}
                            </p>
                        )}
                    </div>

                    <label className="flex items-start gap-3 text-sm text-slate-300">
                        <input
                            type="checkbox"
                            checked={acceptPolicy}
                            onChange={(event) => setAcceptPolicy(event.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-white/20 bg-white/10 text-sky-400 focus:ring-sky-400/40"
                        />
                        <span>
                            I accept the{" "}
                            <Link to="#" className="font-medium text-sky-300 underline-offset-2 hover:underline">
                                Terms of Service
                            </Link>{" "}
                            and{" "}
                            <Link to="#" className="font-medium text-sky-300 underline-offset-2 hover:underline">
                                Privacy Policy
                            </Link>
                            .
                        </span>
                    </label>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-sky-400 px-4 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-sky-500/30 transition hover:bg-sky-300 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isLoading ? "Creating account..." : "Create account"}
                    </button>
                </form>

                {error && (
                    <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                        {error}
                    </div>
                )}
            </div>
        </AuthLayout>
    );
};

export default RegisterPage;
